import { useMemo } from 'react';
import { Upload, Crown, Sparkles, ChevronRight, Target, Download } from 'lucide-react';
import { isDue } from '../../lib/storage/review';
import { computeDiagnostic } from '../../lib/progress/diagnostic';
import { puzzleThemesForWeakness } from '../../lib/puzzles/themes';
import { useInstallPrompt, isLikelyIOS } from '../../hooks/useInstallPrompt';
import type { CoachingReport, Exercise, PuzzleAttempt, StoredGame } from '../../types';

export type NavigableTab = 'partie' | 'chesscom' | 'exercices' | 'puzzles' | 'progression';

export interface NavigateParams {
  openGameId?: string;
  puzzleThemes?: string[];
}

export interface TodayTabProps {
  games: StoredGame[];
  exercises: Exercise[];
  reports: Record<string, CoachingReport>;
  puzzleAttempts: PuzzleAttempt[];
  onNavigate: (tab: NavigableTab, params?: NavigateParams) => void;
}

/**
 * Point d'entrée par défaut. Premier lancement (aucune partie connue) :
 * trois façons de démarrer. Ensuite : ce qu'il y a à faire aujourd'hui
 * (exercices dus, parties récemment analysées) — complété par la synchro
 * Chess.com et les puzzles suggérés une fois ces chantiers en place.
 */
export function TodayTab({ games, exercises, reports, puzzleAttempts, onNavigate }: TodayTabProps) {
  const dueExercises = useMemo(() => exercises.filter((e) => isDue(e.review)), [exercises]);
  const recentGames = useMemo(
    () => [...games].sort((a, b) => b.importedAt.localeCompare(a.importedAt)).slice(0, 5),
    [games],
  );
  const diagnostic = useMemo(() => computeDiagnostic({ games, reports, puzzleAttempts }), [games, reports, puzzleAttempts]);
  const { canInstall, promptInstall } = useInstallPrompt();
  const showIOSInstallHint = !canInstall && isLikelyIOS();

  if (games.length === 0) {
    return (
      <section className="panel today-start">
        <h2>Bienvenue</h2>
        <p className="muted">Trois façons de commencer :</p>
        <div className="today-start-cards">
          <button type="button" className="today-start-card" onClick={() => onNavigate('chesscom')}>
            <Crown className="h-5 w-5" aria-hidden="true" />
            <strong>Connecter mon compte Chess.com</strong>
            <span className="muted small">Importe et analyse tes parties récentes automatiquement.</span>
          </button>
          <button type="button" className="today-start-card" onClick={() => onNavigate('partie')}>
            <Upload className="h-5 w-5" aria-hidden="true" />
            <strong>Coller le PGN d'une partie</strong>
            <span className="muted small">Depuis Chess.com (Partage → PGN) ou tout autre site.</span>
          </button>
          <button type="button" className="today-start-card" onClick={() => onNavigate('partie')}>
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            <strong>Découvrir avec la partie de démonstration</strong>
            <span className="muted small">Pour voir comment Magnus analyse, sans rien importer.</span>
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="today-tab">
      <section className="panel">
        <h2>Au programme aujourd'hui</h2>
        {dueExercises.length > 0 ? (
          <button type="button" className="today-row" onClick={() => onNavigate('exercices')}>
            <span>
              <strong>{dueExercises.length}</strong> exercice{dueExercises.length > 1 ? 's' : ''} à réviser
            </span>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <p className="muted">Rien à réviser pour l'instant.</p>
        )}
        {diagnostic.primaryWeakness && (
          <button
            type="button"
            className="today-row"
            onClick={() => onNavigate('puzzles', { puzzleThemes: puzzleThemesForWeakness([diagnostic.primaryWeakness!.theme]) })}
          >
            <span>
              <Target className="h-4 w-4" aria-hidden="true" style={{ marginRight: '0.4rem', verticalAlign: 'text-bottom' }} />
              Cause n°1 : <strong>{diagnostic.primaryWeakness.theme.replace(/_/g, ' ')}</strong> — s'entraîner
            </span>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </section>

      {recentGames.length > 0 && (
        <section className="panel">
          <h2>Parties récentes</h2>
          <ul className="game-candidate-list">
            {recentGames.map((g) => {
              const r = reports[g.game.id];
              return (
                <li key={g.game.id}>
                  <button
                    type="button"
                    className="game-candidate"
                    onClick={() => onNavigate('partie', { openGameId: g.game.id })}
                  >
                    <strong>
                      {g.game.headers.White ?? '?'} vs {g.game.headers.Black ?? '?'}
                    </strong>
                    <span className="muted">
                      {g.game.headers.Date ?? 'Date inconnue'} ·{' '}
                      {r ? `${r.moments.length} moment${r.moments.length > 1 ? 's' : ''} clé${r.moments.length > 1 ? 's' : ''}` : 'analyse en attente'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {(canInstall || showIOSInstallHint) && (
        <section className="panel install-hint">
          {canInstall ? (
            <button type="button" className="file-btn" onClick={promptInstall}>
              <Download className="h-4 w-4" aria-hidden="true" /> Installer l'app
            </button>
          ) : (
            <p className="muted small">
              Pour installer Magnus sur ton iPhone/iPad : bouton Partager, puis « Sur l'écran d'accueil ».
            </p>
          )}
        </section>
      )}
    </div>
  );
}
